import { ICatalog, ICatalogToPack, IPaginatedData, QueryManyBuilder } from "@app/common";
import { crudEndpointsFactory } from "../crud-endpoints.factory";
import { ICatalogToProduct } from "@app/common/src/interfaces/catalog/catalog-to-product.interface";
import { createSocketFactory } from "../socket";
import { providesList } from "../api";
import { ICatalogToPromotion } from "@app/common/src/interfaces/catalog/catalog-to-promotion.interface";
import { ICatalogToExcludedStep } from "@app/common/src/interfaces/catalog/catalog-to-excluded-step.interface";
import { ICatalogToExcludedOption } from "@app/common/src/interfaces/catalog/catalog-to-excluded-option.interface";

const getSocket = createSocketFactory();

export const catalogToPromotionApi = crudEndpointsFactory<ICatalogToPromotion>()('catalog-to-promotions', 'CatalogToPromotions');

export const catalogToProductApi = crudEndpointsFactory<ICatalogToProduct>()('catalog-to-products', 'CatalogToProducts');

export const catalogToPackApi = crudEndpointsFactory<ICatalogToPack>()('catalog-to-packs', 'CatalogToPacks');

export const catalogToExcludedStepApi = crudEndpointsFactory<ICatalogToExcludedStep>()('catalog-to-excluded-steps', 'CatalogToExcludedSteps');

export const catalogToExcludedOptionApi = crudEndpointsFactory<ICatalogToExcludedOption>()('catalog-to-excluded-options', 'CatalogToExcludedOptions');

export const catalogApi = crudEndpointsFactory<ICatalog>()('catalogs', 'Catalogs')
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      publishCatalog: builder.mutation<any, { catalogId: string }>({
        invalidatesTags: ['CatalogSnapshots'],
        query: (body) => ({
          url: `catalogs/${body.catalogId}/publish`,
          method: 'POST',
        }),
      }),
      readManyCatalogs: builder.query<IPaginatedData<ICatalog>, QueryManyBuilder<ICatalog>>({
        providesTags: (result) => providesList(result?.data, 'Catalogs'),
        query: (params) => ({
          url: 'catalogs',
          params,
          method: 'GET',
        }),
        async onCacheEntryAdded(
          params,
          { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
        ) {

          const socket = await getSocket();

          const listener = (payload) => {
            console.log("listener", payload)

            updateCachedData((draft) => {
            });
          }

          try {

            await cacheDataLoaded;

            if (!socket.hasListeners('TEST')) {
              socket.on('TEST', listener);
            }

          } catch (err) {
            console.error(err);
          }

          await cacheEntryRemoved;

          socket.removeListener('TEST', listener);
        },
      }),
      readOneCatalogWithRelations: builder.query<ICatalog, string>({
        queryFn: async (productId, { dispatch }) => {
          const catalog = await dispatch(catalogApi.endpoints.readOneCatalogs.initiate({
            filters: { _id: { $eq: productId } },
            populate: [
              'catalogToShops',
              'catalogToTags',
              'catalogToTags.tag',
              'catalogToConnectors',
              'catalogToConnectors.connector'
            ]
          }, { forceRefetch: true })).unwrap();

          const catalogWithRelations = await dispatch(catalogApi.endpoints.readOneCatalogs.initiate({
            filters: { _id: { $eq: productId } },
            populate: [
              'catalogToExcludedSteps',
              'catalogToExcludedSteps.step',
              'catalogToExcludedOptions',
              'catalogToExcludedOptions.option',
            ]
          }, { forceRefetch: true })).unwrap();

          const paginatedCatalogToPromotions = await dispatch(catalogToPromotionApi.endpoints.readManyCatalogToPromotions.initiate({
            filters: { catalogId: { $eq: catalog._id } },
            populate: [
              'promotion',
              'promotion.tags'
            ]
          }, { forceRefetch: true })).unwrap();

          const paginatedCatalogToProducts = await dispatch(catalogToProductApi.endpoints.readManyCatalogToProducts.initiate({
            filters: { catalogId: { $eq: catalog._id } },
            populate: [
              'product',
              'product.productVariations',
              'product.tags',
              'catalogProductToProductVariations',
              'catalogProductToProductVariations.productVariation',
            ]
          }, { forceRefetch: true })).unwrap();

          const paginatedCatalogToPacks = await dispatch(catalogToPackApi.endpoints.readManyCatalogToPacks.initiate({
            filters: { catalogId: { $eq: catalog._id } },
            populate: [
              'pack',
              'pack.tags'
            ]
          }, { forceRefetch: true })).unwrap();

          const catalogToPromotions = paginatedCatalogToPromotions?.data ?? [];
          const catalogToPacks = paginatedCatalogToPacks?.data ?? [];
          const catalogToProducts = paginatedCatalogToProducts?.data ?? [];
          const catalogToExcludedSteps = catalogWithRelations.catalogToExcludedSteps ?? [];
          const catalogToExcludedOptions = catalogWithRelations.catalogToExcludedOptions ?? [];

          const data: ICatalog = {
            ...catalog,
            catalogToPromotions,
            catalogToPacks,
            catalogToProducts,
            catalogToExcludedSteps,
            catalogToExcludedOptions
          }

          return { data };
        },
      }),
    }),
  });
